import { ShieldCheckIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";

export interface IBasketUpsell {
  id: string;
  label: string;
  description?: string;
  percentage: number;
  category: "warranty";
}

// Default upsell catalogue. TODO: replace with values pulled from
// admin settings (e.g. /admin/upsells) once that endpoint exists.
export const DEFAULT_BASKET_UPSELLS: IBasketUpsell[] = [
  {
    id: "warranty-6m",
    label: "Extended Warranty - 6 Months",
    description: "Covers parts & labour for 6 months from sale.",
    percentage: 5,
    category: "warranty",
  },
  {
    id: "warranty-12m",
    label: "Extended Warranty - 12 Months",
    description: "Covers parts & labour for 12 months from sale.",
    percentage: 10,
    category: "warranty",
  },
  {
    id: "warranty-24m",
    label: "Extended Warranty - 24 Months",
    description: "Covers parts & labour for 24 months from sale.",
    percentage: 20,
    category: "warranty",
  },
  {
    id: "warranty-lifetime",
    label: "Extended Warranty - Lifetime",
    description: "Lifetime cover for the original purchaser.",
    percentage: 30,
    category: "warranty",
  },
];

interface BasketUpsellsProps {
  options: IBasketUpsell[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  purchasePrice: number;
  disabled?: boolean;
}

export default function BasketUpsells({
  options,
  selectedId,
  onSelect,
  purchasePrice,
  disabled,
}: BasketUpsellsProps) {
  const warrantyOptions = options.filter((o) => o.category === "warranty");

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <ShieldCheckIcon
              className="h-4 w-4 text-primary-600"
              aria-hidden="true"
            />
            Upsells
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Offer the customer an extended warranty. Pricing is a percentage of
            the parts purchase price (after discounts, before shipping).
          </p>
        </div>
        {selectedId && (
          <button
            type="button"
            onClick={() => onSelect(null)}
            disabled={disabled}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear selection
          </button>
        )}
      </div>

      {warrantyOptions.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Extended Warranty
          </p>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {warrantyOptions.map((opt) => {
              const isSelected = selectedId === opt.id;
              const amount = purchasePrice * (opt.percentage / 100);
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(isSelected ? null : opt.id)}
                  aria-pressed={isSelected}
                  className={classNames(
                    "flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors",
                    disabled ? "cursor-not-allowed opacity-60" : "hover:border-primary-400",
                    isSelected
                      ? "border-primary-500 bg-primary-50 ring-1 ring-primary-300"
                      : "border-gray-200 bg-white"
                  )}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <span
                      className={classNames(
                        "text-sm font-semibold",
                        isSelected ? "text-primary-700" : "text-gray-800"
                      )}
                    >
                      {opt.label.replace("Extended Warranty - ", "")}
                    </span>
                    <span
                      className={classNames(
                        "rounded-full px-2 py-0.5 text-xs font-medium",
                        isSelected
                          ? "bg-primary-600 text-white"
                          : "bg-gray-100 text-gray-700"
                      )}
                    >
                      {opt.percentage}%
                    </span>
                  </div>
                  {opt.description && (
                    <p className="text-xs text-gray-500">{opt.description}</p>
                  )}
                  <p
                    className={classNames(
                      "mt-1 text-xs font-medium",
                      isSelected ? "text-primary-700" : "text-gray-600"
                    )}
                  >
                    +{PriceFormat(amount)}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
